import {
  getCurrentUser,
  refreshCurrentUser,
} from '../auth.js';
import { getPlanById } from '../plans.js';
import {
  getBillingInvoices,
  getBillingPaymentMethod,
  createPaymentMethodSetup,
  updatePaymentMethod,
  getBillingHistory,
  updateUserRequest,
  topupPackageM,
} from '../api.js';
import { getBillingCopy, getCommonCopy } from '../i18n.js';
import { showToast } from '../utils/toast.js';

const html = String.raw;

const config = window.__ANFORDERUNGSAI_CONFIG__ ?? {};

const formatCurrency = (amount, currency) => {
  const euros = amount / 100;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency?.toUpperCase() || 'EUR',
  }).format(euros);
};

const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  const date = new Date(timestamp * 1000);
  return new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateRange = (startTimestamp, endTimestamp) => {
  if (!startTimestamp || !endTimestamp) return '-';
  const start = formatDate(startTimestamp);
  const end = formatDate(endTimestamp);
  return `${start} - ${end}`;
};

const getStatusBadge = (status) => {
  const statusMap = {
    paid: { text: 'Bezahlt', class: 'status-success' },
    open: { text: 'Offen', class: 'status-warning' },
    void: { text: 'Storniert', class: 'status-muted' },
    uncollectible: { text: 'Uneinbringlich', class: 'status-error' },
    active: { text: 'Aktiv', class: 'status-success' },
    trialing: { text: 'Testphase', class: 'status-info' },
    canceled: { text: 'Gekündigt', class: 'status-muted' },
  };
  const info = statusMap[status] || { text: status, class: 'status-muted' };
  return `<span class="badge ${info.class}">${info.text}</span>`;
};

/**
 * Show payment method update modal with selection
 */
const showUpdatePaymentMethodModal = async (onSuccess) => {
  const copy = getBillingCopy();

  // Create modal overlay
  const modalOverlay = document.createElement('div');
  modalOverlay.className = 'payment-modal-overlay';
  modalOverlay.innerHTML = html`
    <div class="payment-modal payment-modal-large">
      <div class="payment-modal-header">
        <h3>${copy.paymentMethod.updateTitle}</h3>
        <button class="payment-modal-close" aria-label="Schließen">×</button>
      </div>
      <div class="payment-modal-body">
        <div id="payment-selection-container">
          <p class="muted small">Wird geladen...</p>
        </div>
        <div id="payment-element-container" style="display:none;"></div>
        <div id="payment-error" class="payment-error" style="display:none;"></div>
      </div>
      <div class="payment-modal-footer" id="payment-modal-footer">
        <button type="button" class="button-secondary payment-cancel-btn">Abbrechen</button>
      </div>
    </div>
  `;

  document.body.appendChild(modalOverlay);

  const closeModal = () => {
    modalOverlay.remove();
  };

  const errorEl = modalOverlay.querySelector('#payment-error');
  let cancelBtn = modalOverlay.querySelector('.payment-cancel-btn');
  const closeBtn = modalOverlay.querySelector('.payment-modal-close');

  const showError = (message) => {
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }
  };

  const hideError = () => {
    if (errorEl) {
      errorEl.style.display = 'none';
    }
  };

  // Close handlers
  cancelBtn?.addEventListener('click', closeModal);
  closeBtn?.addEventListener('click', closeModal);
  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) {
      closeModal();
    }
  });

  const selectionContainer = modalOverlay.querySelector('#payment-selection-container');
  const paymentElementContainer = modalOverlay.querySelector('#payment-element-container');
  const footer = modalOverlay.querySelector('#payment-modal-footer');

  let selectedPaymentMethodId = null;
  let mode = 'selection'; // 'selection' or 'add-new'

  // Render selection of existing payment methods
  const renderSelection = async () => {
    try {
      const data = await getBillingPaymentMethod();
      const paymentMethods = data.paymentMethods || [];

      if (paymentMethods.length === 0) {
        // No payment methods, show add new directly
        mode = 'add-new';
        await showAddNewForm();
        return;
      }

      selectionContainer.innerHTML = html`
        <p class="muted small">${copy.paymentMethod.selectDescription || 'Wähle eine Zahlungsmethode aus oder füge eine neue hinzu.'}</p>
        <div class="payment-methods-list">
          ${paymentMethods
            .map(
              (pm) => html`
                <label class="payment-method-option">
                  <input
                    type="radio"
                    name="payment-method"
                    value="${pm.id}"
                    ${pm.isDefault ? 'checked' : ''}
                  />
                  <div class="payment-method-info">
                    <div class="payment-method-brand">${pm.brand?.toUpperCase() || 'KARTE'}</div>
                    <div class="payment-method-number">•••• ${pm.last4 || '????'}</div>
                    <div class="payment-method-expiry">${copy.paymentMethod.expires || 'Exp'}: ${pm.expMonth}/${pm.expYear}</div>
                  </div>
                  ${pm.isDefault ? '<span class="badge status-success">Standard</span>' : ''}
                </label>
              `,
            )
            .join('')}
        </div>
        <button type="button" class="button-secondary" id="add-new-payment-btn" style="margin-top: 16px;">
          + Neue Zahlungsmethode hinzufügen
        </button>
      `;

      // Footer for selection mode
      footer.innerHTML = html`
        <button type="button" class="button-secondary payment-cancel-btn">Abbrechen</button>
        <button type="button" class="button-primary" id="save-selection-btn">Als Standard festlegen</button>
      `;

      // Event listeners
      cancelBtn = footer.querySelector('.payment-cancel-btn');
      cancelBtn?.addEventListener('click', closeModal);

      const addNewBtn = selectionContainer.querySelector('#add-new-payment-btn');
      addNewBtn?.addEventListener('click', () => {
        mode = 'add-new';
        showAddNewForm();
      });

      const saveBtn = footer.querySelector('#save-selection-btn');
      saveBtn?.addEventListener('click', async () => {
        const selected = selectionContainer.querySelector('input[name="payment-method"]:checked');
        if (!selected) {
          showError('Bitte wähle eine Zahlungsmethode aus.');
          return;
        }

        try {
          saveBtn.disabled = true;
          saveBtn.textContent = 'Wird gespeichert...';
          hideError();

          await updatePaymentMethod({ paymentMethodId: selected.value });
          showToast('Standard-Zahlungsmethode aktualisiert!', 'success');
          closeModal();
          await refreshCurrentUser();

          if (onSuccess) {
            onSuccess();
          }
        } catch (err) {
          console.error('Failed to update payment method', err);
          showError(err instanceof Error ? err.message : 'Aktualisierung fehlgeschlagen');
          saveBtn.disabled = false;
          saveBtn.textContent = 'Als Standard festlegen';
        }
      });
    } catch (error) {
      console.error('Failed to load payment methods', error);
      showError('Zahlungsmethoden konnten nicht geladen werden.');
    }
  };

  // Show add new payment method form
  const showAddNewForm = async () => {
    try {
      const stripePublishableKey = config.stripePublishableKey;
      if (!stripePublishableKey || stripePublishableKey === 'YOUR_STRIPE_PUBLISHABLE_KEY_HERE') {
        throw new Error('Stripe Publishable Key nicht konfiguriert');
      }

      if (!window.Stripe) {
        throw new Error('Stripe.js nicht geladen');
      }

      const stripe = window.Stripe(stripePublishableKey);

      // Hide selection, show payment element
      selectionContainer.style.display = 'none';
      paymentElementContainer.style.display = 'block';

      // Update footer
      footer.innerHTML = html`
        <button type="button" class="button-secondary" id="back-to-selection-btn">Zurück</button>
        <button type="button" class="button-primary" id="save-new-payment-btn">Zahlungsmethode speichern</button>
      `;

      const backBtn = footer.querySelector('#back-to-selection-btn');
      const saveNewBtn = footer.querySelector('#save-new-payment-btn');

      backBtn?.addEventListener('click', () => {
        mode = 'selection';
        selectionContainer.style.display = 'block';
        paymentElementContainer.style.display = 'none';
        renderSelection();
      });

      // Create setup intent
      saveNewBtn.disabled = true;
      saveNewBtn.textContent = 'Wird vorbereitet...';

      const response = await createPaymentMethodSetup();

      if (!response?.clientSecret) {
        throw new Error('Client Secret fehlt in der Antwort');
      }

      // Initialize Stripe Elements
      const appearance = {
        theme: 'stripe',
        variables: {
          colorPrimary: '#0066ff',
          fontFamily: 'Inter, system-ui, sans-serif',
        },
      };

      const elements = stripe.elements({
        clientSecret: response.clientSecret,
        appearance,
      });

      const paymentElement = elements.create('payment');
      paymentElement.mount('#payment-element-container');

      saveNewBtn.disabled = false;
      saveNewBtn.textContent = 'Zahlungsmethode speichern';

      saveNewBtn?.addEventListener('click', async () => {
        hideError();
        saveNewBtn.disabled = true;
        saveNewBtn.textContent = 'Wird gespeichert...';

        try {
          const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            redirect: 'if_required',
            confirmParams: {
              return_url: window.location.href,
            },
          });

          if (error) {
            showError(error.message || 'Speichern fehlgeschlagen');
            saveNewBtn.disabled = false;
            saveNewBtn.textContent = 'Zahlungsmethode speichern';
          } else if (setupIntent?.payment_method) {
            await updatePaymentMethod({ paymentMethodId: setupIntent.payment_method });
            showToast('Zahlungsmethode hinzugefügt!', 'success');
            closeModal();
            await refreshCurrentUser();

            if (onSuccess) {
              onSuccess();
            }
          }
        } catch (err) {
          console.error('Setup confirmation error:', err);
          showError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
          saveNewBtn.disabled = false;
          saveNewBtn.textContent = 'Zahlungsmethode speichern';
        }
      });
    } catch (error) {
      console.error('Failed to show add new form', error);
      showError(error instanceof Error ? error.message : 'Fehler beim Laden des Formulars');
    }
  };

  // Start by showing selection
  renderSelection();
};

export const renderBillingView = (container) => {
  const copy = getBillingCopy();
  const commonCopy = getCommonCopy();
  const user = getCurrentUser();

  if (!user) {
    container.innerHTML = `<div class="card"><p class="muted">${commonCopy.authRequired}</p></div>`;
    return;
  }

  const state = {
    activeTab: 'overview', // overview, invoices, payment
    invoices: null,
    history: null,
    paymentMethod: null,
    loading: {
      invoices: false,
      history: false,
      paymentMethod: false,
    },
  };

  const render = () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      container.innerHTML = `<div class="card"><p class="muted">${commonCopy.authRequired}</p></div>`;
      return;
    }

    if (!state.paymentMethod && !state.loading.paymentMethod) {
      state.loading.paymentMethod = true;
      getBillingPaymentMethod()
        .then((data) => {
          state.paymentMethod = data?.defaultPaymentMethod || null;
          state.loading.paymentMethod = false;
          render();
        })
        .catch((error) => {
          console.error('Failed to load payment method', error);
          state.loading.paymentMethod = false;
        });
    }

    const currentPlan = getPlanById(currentUser.planId || 'free');
    const billing = state.paymentMethod || currentUser.billing;
    const subscriptionId = currentUser.stripeSubscriptionId;

    container.innerHTML = html`
      <div class="tabs">
        <button
          class="tab ${state.activeTab === 'overview' ? 'active' : ''}"
          data-tab="overview"
        >
          ${copy.tabs.overview}
        </button>
        <button
          class="tab ${state.activeTab === 'invoices' ? 'active' : ''}"
          data-tab="invoices"
        >
          ${copy.tabs.invoices}
        </button>
        <button
          class="tab ${state.activeTab === 'payment' ? 'active' : ''}"
          data-tab="payment"
        >
          ${copy.tabs.paymentMethod}
        </button>
      </div>

      <div class="tab-content">
        ${renderTabContent(currentUser, currentPlan, billing, subscriptionId)}
      </div>
    `;

    // Attach tab click handlers
    container.querySelectorAll('[data-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        state.activeTab = tab.dataset.tab;
        render();
      });
    });

    // Attach content-specific handlers
    attachHandlers(container);
  };

  const renderTabContent = (user, plan, billing, subscriptionId) => {
    switch (state.activeTab) {
      case 'overview':
        return renderOverview(user, plan, billing, subscriptionId);
      case 'invoices':
        return renderInvoices();
      case 'payment':
        return renderPaymentMethod(billing);
      default:
        return '';
    }
  };

  const renderOverview = (user, plan, billing, subscriptionId) => {
    const hasBilling = Boolean(billing?.method);
    const showPaygPreference = user.planId === 'payg' || user.pendingPlanId === 'payg';
    const paygInvoiceMode = user.paygInvoiceMode ?? 'per_use';
    const isPackageM = user.planId === 'package_m';
    const packageMIncludedUsed = Number(user.packageMIncludedUsed ?? 0);
    const packageMTopupRemaining = Number(user.packageMTopupRemaining ?? 0);
    const packageMPeriod = user.packageMPeriod ?? '-';

    return html`
      <div class="card">
        <h3>${copy.overview.title}</h3>

        <div class="info-grid">
          <div class="info-item">
            <span class="label">${copy.overview.currentPlan}</span>
            <span class="value">
              <div class="pill">${plan?.name || 'Unbekannt'}</div>
            </span>
          </div>

          <div class="info-item">
            <span class="label">${copy.overview.status}</span>
            <span class="value">${getStatusBadge(user.billingStatus || 'none')}</span>
          </div>

          ${subscriptionId
            ? html`
              <div class="info-item">
                <span class="label">${copy.overview.subscriptionId}</span>
                <span class="value code">${subscriptionId}</span>
              </div>
            `
            : ''
          }

          <div class="info-item">
            <span class="label">${copy.overview.paymentMethod}</span>
            <span class="value">
              ${hasBilling
                ? `${billing.brand?.toUpperCase() || 'KARTE'} •••• ${billing.last4 || '????'}`
                : copy.overview.noPaymentMethod
              }
            </span>
          </div>
        </div>

        <div style="margin-top: 24px;">
          <a href="#/profile?tab=plan&action=change-plan" class="button-secondary">${copy.overview.changePlan}</a>
        </div>

        ${showPaygPreference
          ? html`
            <div style="margin-top: 28px;" data-payg-invoice-mode>
              <h4 style="margin-bottom: 6px;">${copy.preference.title}</h4>
              <p class="muted small" style="margin-top: 0;">${copy.preference.description}</p>
              <label style="display:block; margin: 12px 0;">
                <input
                  type="radio"
                  name="payg-invoice-mode"
                  value="per_use"
                  ${paygInvoiceMode === 'per_use' ? 'checked' : ''}
                />
                <strong>${copy.preference.options.perUse.title}</strong>
                <div class="muted small">${copy.preference.options.perUse.description}</div>
              </label>
              <label style="display:block; margin: 12px 0;">
                <input
                  type="radio"
                  name="payg-invoice-mode"
                  value="monthly_summary"
                  ${paygInvoiceMode === 'monthly_summary' ? 'checked' : ''}
                />
                <strong>${copy.preference.options.monthly.title}</strong>
                <div class="muted small">${copy.preference.options.monthly.description}</div>
              </label>
            </div>
          `
          : ''
        }

        ${isPackageM
          ? html`
            <div style="margin-top: 28px;" data-package-m-topup>
              <h4 style="margin-bottom: 6px;">Paket M Kontingent</h4>
              <p class="muted small" style="margin-top:0;">Periode: ${packageMPeriod}</p>
              <p class="muted small" style="margin-top:0;">Inklusive genutzt: ${packageMIncludedUsed}/10</p>
              <p class="muted small" style="margin-top:0;">Top-up Rest: ${packageMTopupRemaining}</p>
              <button type="button" class="button-secondary" data-topup-package-m>+10 Jobs nachbuchen</button>
            </div>
          `
          : ''
        }
      </div>
    `;
  };

  const renderInvoices = () => {
    if (state.loading.invoices) {
      return html`
        <div class="card">
          <p class="muted">${copy.invoices.loading}</p>
        </div>
      `;
    }

    if (!state.invoices) {
      // Load invoices
      state.loading.invoices = true;
      render();

      getBillingInvoices()
        .then((data) => {
          state.invoices = data.invoices || [];
          state.loading.invoices = false;
          render();
        })
        .catch((error) => {
          console.error('Failed to load invoices', error);
          showToast(copy.invoices.loadError, 'error');
          state.invoices = [];
          state.loading.invoices = false;
          render();
        });

      return html`
        <div class="card">
          <p class="muted">${copy.invoices.loading}</p>
        </div>
      `;
    }

    if (!state.invoices.length) {
      return html`
        <div class="card">
          <h3>${copy.invoices.title}</h3>
          <p class="muted">${copy.invoices.noInvoices}</p>
        </div>
      `;
    }

    return html`
      <div class="card">
        <h3>${copy.invoices.title}</h3>
        <table class="data-table">
          <thead>
            <tr>
              <th>${copy.invoices.table.date}</th>
              <th>${copy.invoices.table.description}</th>
              <th>${copy.invoices.table.amount}</th>
              <th>${copy.invoices.table.status}</th>
              <th>${copy.invoices.table.actions}</th>
            </tr>
          </thead>
          <tbody>
            ${state.invoices
              .map(
                (invoice) => html`
                  <tr>
                    <td>${formatDate(invoice.created)}</td>
                    <td>${invoice.description || invoice.plan_name}</td>
                    <td>${formatCurrency(invoice.amount, invoice.currency)}</td>
                    <td>${getStatusBadge(invoice.status)}</td>
                    <td>
                      ${invoice.invoice_pdf
                        ? html`<a
                            href="${invoice.invoice_pdf}"
                            target="_blank"
                            class="button-small button-secondary"
                          >
                            PDF
                          </a>`
                        : '-'
                      }
                    </td>
                  </tr>
                `,
              )
              .join('')}
          </tbody>
        </table>
      </div>
    `;
  };

  const renderPaymentMethod = (billing) => {
    const hasBilling = Boolean(billing?.method);

    return html`
      <div class="card">
        <h3>${copy.paymentMethod.title}</h3>

        ${hasBilling
          ? html`
            <div class="payment-method-display">
              <div class="payment-method-card">
                <div class="payment-method-icon">💳</div>
                <div class="payment-method-details">
                  <div class="payment-method-brand">${billing.brand?.toUpperCase() || 'KARTE'}</div>
                  <div class="payment-method-number">•••• •••• •••• ${billing.last4 || '????'}</div>
                  <div class="payment-method-expiry">
                    ${copy.paymentMethod.expires}: ${billing.expMonth}/${billing.expYear}
                  </div>
                </div>
              </div>
            </div>
          `
          : html`
            <p class="muted">${copy.paymentMethod.noMethod}</p>
          `
        }

        <div style="margin-top: 24px;">
          <button
            type="button"
            class="button-primary"
            data-update-payment-method
          >
            ${hasBilling ? copy.paymentMethod.updateButton : copy.paymentMethod.addButton}
          </button>
        </div>
      </div>
    `;
  };

  const attachHandlers = (container) => {
    const updatePaymentBtn = container.querySelector('[data-update-payment-method]');
    if (updatePaymentBtn) {
      updatePaymentBtn.addEventListener('click', async () => {
        await showUpdatePaymentMethodModal(() => {
          render();
        });
      });
    }

    const preferenceContainer = container.querySelector('[data-payg-invoice-mode]');
    if (preferenceContainer) {
      preferenceContainer.addEventListener('change', async (event) => {
        const target = event.target;
        if (!target || target.name !== 'payg-invoice-mode') {
          return;
        }
        const current = getCurrentUser();
        if (!current) {
          return;
        }
        const selectedMode = target.value;
        if (!selectedMode || selectedMode === current.paygInvoiceMode) {
          return;
        }
        const inputs = preferenceContainer.querySelectorAll('input[name="payg-invoice-mode"]');
        inputs.forEach((input) => {
          input.disabled = true;
        });

        try {
          await updateUserRequest(current.email, { paygInvoiceMode: selectedMode });
          await refreshCurrentUser();
          showToast(copy.preference.saved, 'success');
        } catch (error) {
          console.error('Failed to update payg invoice mode', error);
          showToast(copy.preference.saveError, 'error');
        } finally {
          inputs.forEach((input) => {
            input.disabled = false;
          });
          render();
        }
      });
    }

    const topupButton = container.querySelector('[data-topup-package-m]');
    if (topupButton) {
      topupButton.addEventListener('click', async () => {
        const current = getCurrentUser();
        if (!current) return;
        topupButton.disabled = true;
        topupButton.textContent = 'Wird berechnet...';
        try {
          await topupPackageM({ topupUnits: 10 });
          await refreshCurrentUser();
          showToast('Paket M Top-up erfolgreich.', 'success');
        } catch (error) {
          console.error('Failed to topup package_m', error);
          showToast(error instanceof Error ? error.message : 'Top-up fehlgeschlagen.', 'error');
        } finally {
          topupButton.disabled = false;
          topupButton.textContent = '+10 Jobs nachbuchen';
          render();
        }
      });
    }
  };

  render();
};
